import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, Trash2, Flame } from "lucide-react";
import { useAuth } from "@/lib/auth";
import ConfirmDialog from "./ConfirmDialog";

/**
 * Card per l'upload del file .xlsx PSV/TTF Day Ahead Mid.
 * Riconosce automaticamente il formato storico multi-foglio e quello di aggiornamento
 * mensile "Export". Idempotente su data (le righe con stessa data vengono sovrascritte).
 */
export default function SpotGasUploadCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const inputRef = useRef(null);

  const loadUploads = async () => {
    try {
      const { data } = await api.get("/spot-gas/uploads");
      setUploads(data);
    } catch (e) {
      // silent
    }
  };
  useEffect(() => { loadUploads(); }, []);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/spot-gas/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Importate ${data.rows_imported} righe (${data.date_min} → ${data.date_max})`);
      setFile(null);
      loadUploads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore importazione");
    } finally {
      setBusy(false);
    }
  };

  const deleteUpload = (u) => {
    setConfirmState({
      title: "Eliminare questo caricamento?",
      message: `Verranno rimosse ${u.rows_count} righe dal periodo ${u.date_min} → ${u.date_max}. L'operazione non è reversibile.`,
      confirmLabel: "Elimina",
      variant: "danger",
      onConfirm: async () => {
        try {
          const { data } = await api.delete(`/spot-gas/uploads/${u.id}`);
          toast.success(`${data.deleted_rows} righe rimosse`);
          loadUploads();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        }
      },
    });
  };

  return (
    <div className="card-elevated p-6" data-testid="spot-gas-upload-card">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/30 flex-shrink-0">
          <Flame className="text-white" size={22} />
        </div>
        <div>
          <div className="font-display font-bold text-lg text-slate-900">Prezzi PSV / TTF Day Ahead Mid</div>
          <div className="text-sm text-slate-500">
            Carica lo storico o l&apos;export mensile. Il parser riconosce automaticamente il formato.
            Le righe con stessa data vengono sovrascritte (upsert).
          </div>
        </div>
      </div>

      <div
        onClick={() => inputRef.current?.click()}
        onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50/50 rounded-xl p-8 text-center cursor-pointer transition"
        data-testid="spot-upload-dropzone"
      >
        <FileSpreadsheet className="mx-auto text-blue-600 mb-2" size={32} />
        {file ? (
          <div>
            <div className="font-semibold text-slate-900 text-sm">{file.name}</div>
            <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
          </div>
        ) : (
          <>
            <div className="font-semibold text-slate-900 text-sm">Trascina qui il file Excel</div>
            <div className="text-xs text-slate-500 mt-1">oppure clicca per selezionarlo (.xlsx)</div>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx"
          hidden
          onChange={(e) => setFile(e.target.files[0])}
          data-testid="spot-upload-input"
        />
      </div>

      <div className="mt-4 flex gap-2">
        <button
          disabled={!file || busy}
          onClick={upload}
          className="btn-brand flex items-center gap-2 text-sm"
          data-testid="spot-upload-submit"
        >
          <Upload size={15} /> {busy ? "Importazione…" : "Importa PSV/TTF"}
        </button>
        {file && !busy && (
          <button
            onClick={() => { setFile(null); setResult(null); }}
            className="text-sm px-4 border border-slate-200 rounded-lg hover:bg-slate-100"
          >Reset</button>
        )}
      </div>

      {result && (
        <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
          <div className="flex items-center gap-2 font-semibold">
            <CheckCircle2 size={14} /> Importate {result.rows_imported} righe
          </div>
          <div className="mt-1 text-emerald-700">
            Periodo: {result.date_min} → {result.date_max}
          </div>
        </div>
      )}

      {uploads.length > 0 && (
        <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Ultimi caricamenti</div>
          <ul className="divide-y divide-slate-100 text-sm">
            {uploads.slice(0, 5).map((u) => (
              <li key={u.id} className="py-2 flex items-center gap-3" data-testid={`spot-upload-row-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-slate-500 truncate">{u.filename}</div>
                  <div className="text-xs text-slate-500">
                    {u.rows_count} righe · {u.date_min} → {u.date_max} ·{" "}
                    <span className="text-slate-400">{new Date(u.uploaded_at).toLocaleString("it-IT")}</span>
                  </div>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => deleteUpload(u)}
                    className="p-1.5 text-red-500 hover:text-red-700"
                    title="Elimina upload"
                    data-testid={`spot-upload-delete-${u.id}`}
                  ><Trash2 size={14} /></button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}
